import React, { useRef, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Modal } from "antd";
import { useAuth } from "../context/authContext";
import { useOnboardingData } from "../context/OnboardingDataContext";
import { deleteCookie } from "./deleteCookie";
import { ONBOARDING_SESSION_KEY } from "../../constants";

const NavigationProtection = () => {
  const [showModal, setShowModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { token, clearToken } = useAuth();
  const { clearOnboardingData } = useOnboardingData();
  const isNavigatingRef = useRef(false);
  const currentPathRef = useRef(location.pathname);
  const gestureDetectedRef = useRef(false);

  const needsProtection = location.pathname.startsWith("/digital-onboarding/");

  useEffect(() => {
    if (token) {
      sessionStorage.setItem(ONBOARDING_SESSION_KEY, "true");
    }
  }, [token]);

  useEffect(() => {
    currentPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!needsProtection) return;

    const hadSession =
      sessionStorage.getItem(ONBOARDING_SESSION_KEY) === "true";

    if (!token && hadSession) {
      setShowModal(true);
      return;
    }

    if (!token) return;

    const handlePopState = (event) => {
      if (gestureDetectedRef.current) {
        gestureDetectedRef.current = false;
        return;
      }

      if (!isNavigatingRef.current) {
        event.preventDefault();

        window.history.pushState(
          { protected: true, path: currentPathRef.current },
          "",
          currentPathRef.current
        );
        window.history.pushState(
          { protected: true, path: currentPathRef.current },
          "",
          currentPathRef.current
        );

        setShowModal(true);
      }
    };

    window.history.pushState(
      { protected: true, path: location.pathname },
      "",
      location.pathname
    );
    window.history.pushState(
      { protected: true, path: location.pathname },
      "",
      location.pathname
    );

    window.addEventListener("popstate", handlePopState);

    let touchStartX = 0;
    let touchStartY = 0;
    const edgeThreshold = 50;
    const swipeThreshold = 50;

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };

    const handleTouchMove = (e) => {
      const touchEndX = e.touches[0].clientX;
      const touchEndY = e.touches[0].clientY;
      const deltaX = touchEndX - touchStartX;
      const deltaY = Math.abs(touchEndY - touchStartY);

      const screenWidth = window.innerWidth;

      const isLeftEdgeSwipe =
        touchStartX < edgeThreshold &&
        deltaX > swipeThreshold &&
        deltaY < swipeThreshold;

      const isRightEdgeSwipe =
        touchStartX > screenWidth - edgeThreshold &&
        deltaX < -swipeThreshold &&
        deltaY < swipeThreshold;

      if (isLeftEdgeSwipe || isRightEdgeSwipe) {
        e.preventDefault();

        gestureDetectedRef.current = true;

        setTimeout(() => {
          gestureDetectedRef.current = false;
        }, 300);

        window.history.pushState(
          { protected: true, path: currentPathRef.current },
          "",
          currentPathRef.current
        );
        window.history.pushState(
          { protected: true, path: currentPathRef.current },
          "",
          currentPathRef.current
        );

        setShowModal(true);
      }
    };

    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    document.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });

    return () => {
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
    };
  }, [needsProtection, token, location.pathname]);

  const handleOkClick = () => {
    isNavigatingRef.current = true;

    clearToken();
    clearOnboardingData();
    sessionStorage.removeItem(ONBOARDING_SESSION_KEY);
    deleteCookie("auth_token");

    setShowModal(false);

    setTimeout(() => {
      navigate("/digital-onboarding", { replace: true });
      isNavigatingRef.current = false;
    }, 0);
  };

  return (
    <Modal
      title="You've Been Logged Out"
      open={showModal}
      onOk={handleOkClick}
      okText="OK"
      cancelButtonProps={{ style: { display: "none" } }}
      closable={false}
      maskClosable={false}
      keyboard={false}
      okButtonProps={{ style: { width: "200px" } }}
      footer={(_, { OkBtn }) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <OkBtn />
        </div>
      )}
    >
      <p>
        For security reasons, you have been logged out of the Registration
        Application. This happens when you refresh the page or use the back
        button on the browser.
      </p>
    </Modal>
  );
};

export default NavigationProtection;
